<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
/***/
import { useApplicationStore } from '@stores/application';
/***/
import { useInitAuthPage } from '@compositions/Auth';
import { useCountry } from '@compositions/Country';
import { useTimeZone } from '@compositions/TimeZone';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const RegisterForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/Register/RegisterForm'));
const ResendActivationButton = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/Register/ResendActivationButton'));
const Icon = defineAsyncComponent(() => import(/* webpackChunkName: "components" */'@components/UI/Icon'));
const LegalFiles = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/LegalFiles'));
const RightImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/RightImage'));
const AuthContentImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/ContentImage'));

// Stores
const applicationStore = useApplicationStore();
const currentApplication = computed(() => applicationStore.getCurrent);

// I18n
const { t, locale } = useI18n();

// Route
const route = useRoute();

// Type d'utilisateur en paramètre de la route
const userTypeParam = (route.params.userType ?? null);

// Type d'utilisateur
const userType = computed(() => {
    if(userTypeParam === 'agency') {
        return 'AGENCY_MANAGER';
    }
    return null;
});

// Redirige l'utilisateur s'il est déjà connecté
const { authPreparing } = useInitAuthPage({});

// Vrai s'il y a eu une erreur lors de l'envoi du mail d'activation
const sendActivationMailError = ref(false);

// Vrai si le mail d'activation a été envoyé
const activationMailSent = ref(false);

// Utilisateur enregistré
const registeredUser = ref(null);

// Composition pour le chargement des pays
const {
    loadCountriesError,
    loadCountries
} = useCountry({});

// Composition pour le chargement des timezones
const {
    loadTimeZonesError,
    loadTimeZones
} = useTimeZone({});

const countriesReady = ref(false);
const timeZonesReady = ref(false);

onMounted(() => {
    document.title = t('page.auth.register.title') + ' - ' + currentApplication.value.name;

    loadCountries().then(() => {
        countriesReady.value = true;
    });

    loadTimeZones().then(() => {
        timeZonesReady.value = true;
    });
});

</script>

<template>

    <p
        v-if="loadCountriesError || loadTimeZonesError"
        class="generic-message generic-message-error"
    >
        {{ $t('auth.register.loading.resources.error') }}
    </p>

    <loader v-else-if="authPreparing || ! countriesReady || ! timeZonesReady"></loader>

    <div
        v-else
        class="auth-container"
        :class="{ 'registration-validation': registeredUser }"
    >
        <auth-content-image></auth-content-image>
        <div class="auth_content">
            <section
                v-if="registeredUser !== null"
                class="registered"
            >
                <h2>{{ $t('auth.register.registered.title') }}</h2>
                <p class="advice">{{ $t('auth.register.registered.advice') }}</p>
                <p class="without-notification">
                    {{ $t('auth.register.registered.withoutNotification') }}
                    <resend-activation-button
                        :user="registeredUser"
                        v-on:update:error="sendActivationMailError = $event"
                        v-on:update:success="activationMailSent = $event"
                    ></resend-activation-button>
                    <p
                        v-if="sendActivationMailError"
                        class="error"
                    >
                        {{ $t('auth.register.registered.sendActivationMail.error') }}
                    </p>
                    <p
                        v-else-if="registeredUser && activationMailSent"
                        class="success"
                    >
                        {{ $t('auth.register.registered.sendActivationMail.success', {
                            email: registeredUser.email
                        }) }}
                    </p>
                </p>
                <div class="auth_footer">
                    <legal-files></legal-files>
                </div>
            </section>
            <template v-else>
                <div class="auth_title">
                    <h1 class="first-title">{{ $t('auth.register.form.title') }}</h1>
                    <p>
                        {{ $t('auth.register.form.login.call') }}
                        <router-link
                            :to="{ name: 'auth.login' }"
                            title=""
                        >
                            {{ $t('auth.register.form.login.button.label') }}
                        </router-link>
                    </p>
                </div>
                <register-form
                    @registered="registeredUser = $event"
                    :user-type="userType"
                ></register-form>
            </template>
        </div>

        <div v-if="registeredUser" class="auth_img"></div>
        <right-image v-else></right-image>

    </div>
</template>
